import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "../../i18n/LanguageContext";
import { useAuth } from "../../lib/AuthContext";
import { logCookieConsent } from "../../lib/api";
import { Cookie, Shield, BarChart3, Megaphone, X, Check } from "lucide-react";

const STORAGE_KEY = "aiba_cookie_consent";
const SYNC_KEY = "aiba_cookie_consent_synced";
const VERSION = "1";

// Persist a proof of consent server-side for authenticated users (deduped per choice).
const syncConsentToServer = (payload, user) => {
  if (!user) return;
  const sig = `${payload.version}:${payload.analytics ? 1 : 0}:${payload.marketing ? 1 : 0}`;
  if (localStorage.getItem(SYNC_KEY) === sig) return;
  logCookieConsent({ analytics: !!payload.analytics, marketing: !!payload.marketing, version: payload.version })
    .then(() => localStorage.setItem(SYNC_KEY, sig))
    .catch(() => {});
};

const COPY = {
  fr: {
    title: "Nous respectons votre vie privée",
    body: "Nous utilisons des cookies pour améliorer votre expérience de navigation, sécuriser notre plateforme et analyser son utilisation. Vous pouvez accepter tous les cookies, les refuser ou gérer vos préférences à tout moment. Pour en savoir plus, consultez notre Politique de confidentialité et notre Politique relative aux cookies.",
    privacy: "Politique de confidentialité",
    cookies: "Politique relative aux cookies",
    acceptAll: "Tout accepter",
    rejectAll: "Tout refuser",
    manage: "Gérer les préférences",
    save: "Enregistrer mes choix",
    prefsTitle: "Préférences de cookies",
    prefsIntro: "Choisissez les catégories de cookies que vous autorisez. Les cookies essentiels sont toujours actifs car ils sont nécessaires au fonctionnement du site.",
    always: "Toujours actif",
    cats: {
      essential: { name: "Essentiels", desc: "Nécessaires à la sécurité, à l'authentification et au fonctionnement de base du site." },
      analytics: { name: "Analytiques", desc: "Nous aident à comprendre l'utilisation du site pour l'améliorer (mesure d'audience anonyme)." },
      marketing: { name: "Marketing", desc: "Utilisés pour personnaliser les contenus et mesurer l'efficacité de nos campagnes." },
    },
  },
  en: {
    title: "We respect your privacy",
    body: "We use cookies that are essential for the site to work. With your consent, we also use analytics and marketing cookies. You can accept, reject or customize your choices at any time. No non-essential cookie is activated without your consent.",
    privacy: "Privacy Policy",
    cookies: "Cookie Policy",
    acceptAll: "Accept all",
    rejectAll: "Reject all",
    manage: "Manage preferences",
    save: "Save my choices",
    prefsTitle: "Cookie preferences",
    prefsIntro: "Choose the cookie categories you allow. Essential cookies are always on because they are required for the site to work.",
    always: "Always on",
    cats: {
      essential: { name: "Essential", desc: "Required for security, authentication and core site functionality." },
      analytics: { name: "Analytics", desc: "Help us understand site usage to improve it (anonymous audience measurement)." },
      marketing: { name: "Marketing", desc: "Used to personalize content and measure the effectiveness of our campaigns." },
    },
  },
};

const CAT_ICON = { essential: Shield, analytics: BarChart3, marketing: Megaphone };

const readConsent = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed.version !== VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
};

const Toggle = ({ checked, disabled, onChange, testid }) => (
  <button
    type="button"
    data-testid={testid}
    disabled={disabled}
    onClick={() => !disabled && onChange(!checked)}
    className={`relative h-6 w-11 shrink-0 rounded-full transition-colors duration-300 ${
      checked ? "bg-blue-500" : "bg-blue-500/15"
    } ${disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
  >
    <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all duration-300 ${checked ? "left-[22px]" : "left-0.5"}`} />
  </button>
);

export const CookieConsent = () => {
  const { lang } = useLang();
  const { user, setAuthOpen } = useAuth();
  const c = COPY[lang] || COPY.en;
  const [visible, setVisible] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: false, marketing: false });

  useEffect(() => {
    if (!readConsent()) setVisible(true);
    const openPrefs = () => {
      const existing = readConsent();
      if (existing) setPrefs({ analytics: !!existing.analytics, marketing: !!existing.marketing });
      setShowPrefs(true);
      setVisible(true);
    };
    window.addEventListener("aiba:cookie-prefs", openPrefs);
    return () => window.removeEventListener("aiba:cookie-prefs", openPrefs);
  }, []);

  // When a consented user is authenticated, associate their stored consent with their account.
  useEffect(() => {
    if (user) {
      const c = readConsent();
      if (c) syncConsentToServer(c, user);
    }
  }, [user]);

  const persist = useCallback((choice) => {
    const payload = { version: VERSION, essential: true, ...choice, date: new Date().toISOString() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    localStorage.removeItem(SYNC_KEY);
    syncConsentToServer(payload, user);
    setVisible(false);
    setShowPrefs(false);
    // After the cookie choice, prompt anonymous visitors to sign in.
    if (!user) setTimeout(() => setAuthOpen(true), 500);
  }, [user, setAuthOpen]);

  const acceptAll = () => persist({ analytics: true, marketing: true });
  const rejectAll = () => persist({ analytics: false, marketing: false });
  const saveChoices = () => persist({ analytics: prefs.analytics, marketing: prefs.marketing });

  if (!visible) return null;

  return (
    <>
      {!showPrefs && (
        <motion.div
          data-testid="cookie-banner"
          initial={{ y: 120, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 120, opacity: 0 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="safe-bottom fixed inset-x-0 bottom-0 z-[100] p-4 sm:p-6"
        >
          <div className="mx-auto flex max-w-5xl flex-col gap-5 rounded-2xl border border-blue-500/25 bg-[#0B1220]/95 p-6 backdrop-blur-2xl shadow-2xl lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-start gap-4">
              <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-blue-400/40 bg-blue-400/10">
                <Cookie size={18} className="text-blue-300" />
              </span>
              <div>
                <h3 className="font-display text-base font-700 tracking-tight text-white">{c.title}</h3>
                <p className="mt-1.5 max-w-2xl font-body text-[13px] leading-relaxed text-[#8AA0C0]">{c.body}</p>
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
                  <Link to="/confidentialite" data-testid="cookie-privacy-link" className="font-mono text-[10px] uppercase tracking-[0.15em] text-blue-300 underline hover:text-blue-200">{c.privacy}</Link>
                  <Link to="/cookies" data-testid="cookie-policy-link" className="font-mono text-[10px] uppercase tracking-[0.15em] text-blue-300 underline hover:text-blue-200">{c.cookies}</Link>
                </div>
              </div>
            </div>
            <div className="flex shrink-0 flex-col gap-2.5 sm:flex-row sm:flex-wrap lg:flex-col xl:flex-row">
              <button data-testid="cookie-reject-all" onClick={rejectAll}
                className="rounded-full border border-blue-500/30 px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.18em] text-[#CBD5E1] transition-colors hover:border-blue-400/50 hover:text-white">
                {c.rejectAll}
              </button>
              <button data-testid="cookie-manage" onClick={() => setShowPrefs(true)}
                className="rounded-full border border-blue-500/30 px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.18em] text-blue-200 transition-colors hover:border-blue-400/50 hover:text-white">
                {c.manage}
              </button>
              <button data-testid="cookie-accept-all" onClick={acceptAll}
                className="rounded-full bg-blue-600 px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.18em] text-white cta-glow transition-all hover:bg-blue-500">
                {c.acceptAll}
              </button>
            </div>
          </div>
        </motion.div>
      )}

      <AnimatePresence>
        {showPrefs && (
          <motion.div
            data-testid="cookie-prefs-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[110] flex items-end justify-center bg-black/60 p-4 backdrop-blur-sm sm:items-center"
          >
            <motion.div
              initial={{ y: 40, opacity: 0, scale: 0.98 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 40, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="w-full max-w-lg overflow-hidden rounded-2xl border border-blue-500/25 bg-[#0B1220]/97 backdrop-blur-2xl shadow-2xl"
            >
              <div className="flex items-center justify-between border-b border-blue-500/15 px-6 py-5">
                <h3 className="flex items-center gap-2 font-display text-lg font-700 tracking-tight text-white">
                  <Cookie size={18} className="text-blue-300" /> {c.prefsTitle}
                </h3>
                <button data-testid="cookie-prefs-close" onClick={() => { setShowPrefs(false); if (!readConsent()) setVisible(true); }} className="text-[#8AA0C0] hover:text-white">
                  <X size={18} />
                </button>
              </div>
              <div className="max-h-[55vh] space-y-4 overflow-y-auto px-6 py-5">
                <p className="font-body text-[13px] leading-relaxed text-[#8AA0C0]">{c.prefsIntro}</p>
                {["essential", "analytics", "marketing"].map((key) => {
                  const Icon = CAT_ICON[key];
                  const cat = c.cats[key];
                  const isEssential = key === "essential";
                  return (
                    <div key={key} data-testid={`cookie-cat-${key}`} className="flex items-start justify-between gap-4 rounded-xl border border-blue-500/15 bg-blue-400/[0.02] p-4">
                      <div className="flex items-start gap-3">
                        <Icon size={16} className="mt-0.5 text-blue-300" />
                        <div>
                          <p className="font-body text-sm font-600 text-white">{cat.name}</p>
                          <p className="mt-1 font-body text-xs leading-relaxed text-[#8AA0C0]">{cat.desc}</p>
                        </div>
                      </div>
                      {isEssential ? (
                        <span className="shrink-0 font-mono text-[9px] uppercase tracking-[0.15em] text-emerald-300">{c.always}</span>
                      ) : (
                        <Toggle
                          testid={`cookie-toggle-${key}`}
                          checked={prefs[key]}
                          onChange={(v) => setPrefs((p) => ({ ...p, [key]: v }))}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="flex flex-col gap-2.5 border-t border-blue-500/15 px-6 py-5 sm:flex-row sm:justify-end">
                <button data-testid="cookie-prefs-reject" onClick={rejectAll}
                  className="rounded-full border border-blue-500/30 px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.18em] text-[#CBD5E1] hover:border-blue-400/50 hover:text-white">
                  {c.rejectAll}
                </button>
                <button data-testid="cookie-prefs-save" onClick={saveChoices}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-blue-600 px-6 py-2.5 font-mono text-[10px] uppercase tracking-[0.18em] text-white cta-glow hover:bg-blue-500">
                  <Check size={13} /> {c.save}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
